import React, { useCallback, useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import type { AxiosProgressEvent } from 'axios';
import { palette } from '@/styles/figma-tokens';
import StepCard from '../shared/StepCard';
import DropZone from '../shared/DropZone';
import { usePlanFeatures } from '../../../../hooks/usePlanFeatures';
import { fetchCertificatePreview, useUpdateCourse } from '../../../../services/courses';
import { uploadFile } from '../../../../services/uploads';
import { useNavigate } from 'react-router-dom';
import type { CertificationType } from '../../../../types/course';

const MAX_DESIGN_SIZE_MB = 20;
const ALLOWED_DESIGN_TYPES = ['application/pdf', 'image/png', 'image/jpeg'];
const LAYOUT_GUIDE_URL = '/certificates/certificate-layout-guide.pdf';

interface CertificateStepProps {
  courseId: string;
  disabled?: boolean;
  onSaved: () => void;
  /** Stagger delay forwarded to StepCard (seconds). */
  entranceDelay?: number;
  /** True when the wizard is editing an existing course; the step starts collapsed. */
  editMode?: boolean;
  /** Pre-populated values for edit mode. */
  initialCertificationType?: CertificationType | null;
  initialCertificateFileKey?: string | null;
}

// ─── Icons ────────────────────────────────────────────────────────────────────

/** Certificate with an X mark — "No certification" */
function CertNoneIcon() {
  return (
    <svg width="48" height="48" viewBox="0 0 48 48" fill="none" aria-hidden="true">
      {/* Scroll body */}
      <rect x="6" y="10" width="36" height="28" rx="4" stroke={palette.grey['500']} strokeWidth="2.5" fill="none" />
      {/* Ribbon lines */}
      <path d="M12 18h24M12 24h16" stroke={palette.grey['500']} strokeWidth="2" strokeLinecap="round" />
      {/* Rosette circle */}
      <circle cx="34" cy="34" r="7" fill="#fff" stroke={palette.grey['500']} strokeWidth="2" />
      {/* X mark */}
      <path d="M31 31l6 6M37 31l-6 6" stroke={palette.error['500']} strokeWidth="2" strokeLinecap="round" />
    </svg>
  );
}

/** Certificate with sparkles — "Auto-Generated" */
function CertAutoIcon() {
  return (
    <svg width="48" height="48" viewBox="0 0 48 48" fill="none" aria-hidden="true">
      {/* Scroll body */}
      <rect x="6" y="10" width="36" height="28" rx="4" style={{ stroke: 'rgb(var(--color-primary))' }} strokeWidth="2.5" fill="none" />
      {/* Ribbon lines */}
      <path d="M12 18h24M12 24h16" style={{ stroke: 'rgb(var(--color-primary))' }} strokeWidth="2" strokeLinecap="round" />
      {/* Rosette circle */}
      <circle cx="34" cy="34" r="7" fill="rgb(var(--color-primary) / 0.08)" style={{ stroke: 'rgb(var(--color-primary))' }} strokeWidth="2" />
      {/* Sparkle star */}
      <path
        d="M34 28.5v2M34 37.5v2M29.5 34h2M36.5 34h2M31.1 30.1l1.4 1.4M36.5 35.5l1.4 1.4M31.1 37.9l1.4-1.4M36.5 32.5l1.4-1.4"
        style={{ stroke: 'rgb(var(--color-primary))' }}
        strokeWidth="1.5"
        strokeLinecap="round"
      />
    </svg>
  );
}

/** Certificate with upload arrow — "Upload Custom" */
function CertUploadIcon() {
  return (
    <svg width="48" height="48" viewBox="0 0 48 48" fill="none" aria-hidden="true">
      {/* Scroll body */}
      <rect x="6" y="10" width="36" height="28" rx="4" stroke={palette.grey['500']} strokeWidth="2.5" fill="none" />
      {/* Ribbon lines */}
      <path d="M12 18h24M12 24h12" stroke={palette.grey['500']} strokeWidth="2" strokeLinecap="round" />
      {/* Rosette circle */}
      <circle cx="34" cy="34" r="7" fill="rgb(var(--color-primary) / 0.08)" style={{ stroke: 'rgb(var(--color-primary))' }} strokeWidth="2" />
      {/* Upload arrow */}
      <path d="M34 37.5v-5M31.5 34.5l2.5-2.5 2.5 2.5" style={{ stroke: 'rgb(var(--color-primary))' }} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

// ─── Choice Card ──────────────────────────────────────────────────────────────

interface ChoiceCardProps {
  icon: React.ReactNode;
  title: string;
  description: string;
  selected: boolean;
  onClick: () => void;
  /** Plan-locked: visible (staff may see the offer) but not selectable. */
  lockedPlanTag?: string;
}

function ChoiceCard({ icon, title, description, selected, onClick, lockedPlanTag }: ChoiceCardProps) {
  const locked = Boolean(lockedPlanTag);
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={locked}
      title={locked ? `Available from the ${lockedPlanTag} plan` : undefined}
      style={{
        flex: 1,
        minWidth: '180px',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        textAlign: 'center',
        padding: '24px',
        borderRadius: '16px',
        border: `2px solid ${selected ? 'rgb(var(--color-primary))' : 'rgba(189,188,201,0.3)'}`,
        background: selected ? 'rgb(var(--color-primary) / 0.08)' : 'rgb(var(--color-surface-primary))',
        cursor: locked ? 'not-allowed' : 'pointer',
        opacity: locked ? 0.6 : 1,
        transition: 'border-color 0.15s, background 0.15s',
        outline: 'none',
      }}
      onMouseEnter={(e) => {
        if (!selected && !locked) {
          (e.currentTarget as HTMLButtonElement).style.borderColor = 'rgb(var(--color-primary) / 0.45)';
        }
      }}
      onMouseLeave={(e) => {
        if (!selected && !locked) {
          (e.currentTarget as HTMLButtonElement).style.borderColor = 'rgba(189,188,201,0.3)';
        }
      }}
      aria-pressed={selected}
    >
      {/* Icon */}
      <div style={{ marginBottom: '16px' }}>{icon}</div>

      {/* Title */}
      <p
        style={{
          fontFamily: 'Poppins, sans-serif',
          fontSize: '14px',
          fontWeight: 600,
          lineHeight: '22px',
          color: 'rgb(var(--color-text-primary))',
          margin: '0 0 6px',
        }}
      >
        {title}
        {locked && (
          <span
            className="text-primary-600 dark:text-primary-350"
            style={{
              display: 'block',
              fontSize: '11px',
              fontWeight: 700,
              letterSpacing: '0.04em',
              textTransform: 'uppercase',
            }}
          >
            {lockedPlanTag} plan
          </span>
        )}
      </p>

      {/* Description */}
      <p
        style={{
          fontFamily: 'Poppins, sans-serif',
          fontSize: '12px',
          fontWeight: 400,
          lineHeight: '18px',
          color: 'rgb(var(--color-text-secondary))',
          margin: 0,
        }}
      >
        {description}
      </p>
    </button>
  );
}

// ─── CertificateStep ──────────────────────────────────────────────────────────

export default function CertificateStep({
  courseId,
  disabled = false,
  onSaved,
  entranceDelay,
  editMode = false,
  initialCertificationType,
  initialCertificateFileKey,
}: CertificateStepProps) {
  const [choice, setChoice] = useState<CertificationType>(initialCertificationType ?? 'none');
  const [pdfKey, setPdfKey] = useState<string | null>(initialCertificateFileKey ?? null);
  const [pdfName, setPdfName] = useState<string | null>(
    initialCertificateFileKey ? 'Certificate design' : null
  );
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [previewError, setPreviewError] = useState<string | null>(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  // In edit mode the certificate choice was already persisted, so start collapsed.
  const [saved, setSaved] = useState(editMode);

  const { certificates: certificatesAllowed, customCertificates: customAllowed } =
    usePlanFeatures();
  const navigate = useNavigate();
  const updateCourse = useUpdateCourse();

  // Live sample preview: whenever an uploaded design is present, render it
  // with placeholder data exactly the way learners will receive it.
  useEffect(() => {
    if (!pdfKey || choice !== 'custom_upload') {
      setPreviewUrl((old) => {
        if (old) URL.revokeObjectURL(old);
        return null;
      });
      return;
    }
    let cancelled = false;
    setPreviewLoading(true);
    setPreviewError(null);
    (async () => {
      try {
        const blob = await fetchCertificatePreview(courseId, pdfKey);
        if (cancelled) return;
        const url = URL.createObjectURL(blob);
        setPreviewUrl((old) => {
          if (old) URL.revokeObjectURL(old);
          return url;
        });
      } catch (err: unknown) {
        if (cancelled) return;
        // The 422 body is a JSON blob — surface a friendly fixed message.
        const status = (err as { response?: { status?: number } })?.response?.status;
        setPreviewError(
          status === 422
            ? 'We could not read this design. Please upload a single-page PDF or a PNG/JPG image.'
            : 'Preview unavailable right now — the design itself is saved.'
        );
      } finally {
        if (!cancelled) setPreviewLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [pdfKey, choice, courseId]);

  const handleChoiceSelect = (next: CertificationType) => {
    setChoice(next);
    setSaved(false);
  };

  const handlePdfSelected = useCallback(
    async (files: File[]) => {
      const file = files[0];
      if (!file) return;
      if (!ALLOWED_DESIGN_TYPES.includes(file.type)) {
        setUploadError('Please upload a PDF, PNG, or JPG file');
        return;
      }
      setUploadError(null);
      setPdfName(file.name);
      setUploading(true);
      setUploadProgress(0);
      try {
        const result = await uploadFile(
          { file, resource_type: 'course_certificate', resource_id: courseId },
          {
            onUploadProgress: (progressEvent: AxiosProgressEvent) => {
              const pct = Math.round(
                (progressEvent.loaded * 100) / (progressEvent.total || 100)
              );
              setUploadProgress(pct);
            },
          }
        );
        setPdfKey(result.key);
        setUploading(false);
        setUploadProgress(100);
      } catch (err: unknown) {
        const message =
          (err as { response?: { data?: { message?: string } } })?.response?.data
            ?.message ?? 'Upload failed. Please try again.';
        setUploadError(message);
        setUploading(false);
        setPdfName(null);
      }
    },
    [courseId]
  );

  const handleRemovePdf = () => {
    setPdfKey(null);
    setPdfName(null);
    setUploadError(null);
    setUploadProgress(0);
  };

  const handleSave = async () => {
    // The uploaded design IS the certificate: saving 'custom_upload' without
    // one issues nothing at completion, silently (Aleix, 2026-09-10 — he
    // finished the course and his Certificates page stayed empty).
    if (choice === 'custom_upload' && !pdfKey) {
      setUploadError('Upload your certificate design before saving this step.');
      toast.error('Upload your certificate design first.');
      return;
    }
    setSaving(true);
    try {
      await updateCourse.mutateAsync({
        courseId,
        data: {
          certification_type: choice,
          certificate_file_key: choice === 'custom_upload' ? pdfKey : null,
        },
      });
      setSaved(true);
      onSaved();
    } catch (err: unknown) {
      const message =
        (err as { response?: { data?: { message?: string } } })?.response?.data?.message ??
        'Failed to save certification. Please try again.';
      toast.error(message);
    } finally {
      setSaving(false);
    }
  };

  const summary = saved
    ? choice === 'auto_generated'
      ? 'Auto-generated certificate'
      : choice === 'custom_upload'
        ? `Custom upload${pdfName ? ` · ${pdfName}` : ''}`
        : 'No certificate'
    : undefined;

  return (
    <StepCard
      stepNumber={4}
      title="Certification (optional)"
      disabled={disabled}
      onSave={handleSave}
      saving={saving}
      saved={saved}
      summary={summary}
      entranceDelay={entranceDelay}
    >
      {!certificatesAllowed ? (
        /* Free has no certificates at all — mirror GamificationStep's lock
           panel instead of showing cards that look available (Aleix, 8/10). */
        <div className="mx-auto max-w-[480px] px-6 pb-6 pt-4 text-center">
          <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-2xl bg-primary-500/10">
            <svg width="28" height="28" viewBox="0 0 24 24" fill="none" aria-hidden="true">
              <rect x="3" y="11" width="18" height="11" rx="2" stroke="rgb(var(--color-primary))" strokeWidth="2" />
              <path d="M7 11V7a5 5 0 0110 0v4" stroke="rgb(var(--color-primary))" strokeWidth="2" strokeLinecap="round" />
            </svg>
          </div>
          <p className="mb-2 text-base font-bold text-text-primary">
            Completion certificates are available from the Growth plan
          </p>
          <p className="mb-5 text-[13px] leading-5 text-text-secondary">
            Growth includes the auto-generated Tereiko certificate; Pro adds
            your own custom design. On the Free plan this course simply
            completes without one.
          </p>
          <button
            type="button"
            onClick={() => navigate('/settings?tab=billing&section=plan#tier-plans')}
            className="inline-flex items-center rounded-full bg-primary-500 px-5 py-2.5 text-sm font-bold transition-[background-color,transform] duration-150 hover:bg-primary-600 active:scale-[0.97] dark:hover:bg-primary-400"
          >
            Upgrade Plan
          </button>
        </div>
      ) : (
      <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
        {/* Three choice cards in a horizontal row */}
        <div style={{ display: 'flex', gap: '24px', flexWrap: 'wrap' }}>
          <ChoiceCard
            icon={<CertNoneIcon />}
            title="No certification"
            description="There will be no certification for this course"
            selected={choice === 'none'}
            onClick={() => handleChoiceSelect('none')}
          />

          <ChoiceCard
            icon={<CertAutoIcon />}
            title="Auto-Generated Certificate"
            description="We will create the certificate once the learner is completed"
            selected={choice === 'auto_generated'}
            onClick={() => handleChoiceSelect('auto_generated')}
            lockedPlanTag={certificatesAllowed ? undefined : 'Growth'}
          />

          <ChoiceCard
            icon={<CertUploadIcon />}
            title="Upload Custom Certificate"
            description={pdfName ?? 'Upload your own design for this course'}
            selected={choice === 'custom_upload'}
            onClick={() => handleChoiceSelect('custom_upload')}
            lockedPlanTag={customAllowed ? undefined : 'Pro'}
          />
        </div>

        {choice !== 'none' && (
          <p className="mx-auto mt-3 max-w-[520px] text-center text-xs text-text-secondary">
            If this course runs inside a learning program, learners receive the
            program&rsquo;s certificate instead of this one — one certificate for
            the whole journey, not one per course.
          </p>
        )}

        {/* Custom-upload design picker + explanation + live preview */}
        {choice === 'custom_upload' && (
          <div className="rounded-2xl border border-border-primary/20 px-5 py-4 flex flex-col gap-4">
            {/* How it works */}
            <div className="rounded-xl bg-secondary-500/10 border border-secondary-500/30 px-4 py-3 text-[13px] leading-5 text-text-primary">
              <p className="font-semibold mb-1">How custom certificates work</p>
              <p className="text-text-secondary">
                Design a single landscape page (A4 shape) and{' '}
                <span className="font-semibold text-text-primary">
                  leave the middle band empty and light-colored
                </span>{' '}
                — that&rsquo;s where we print each learner&rsquo;s name, the course
                name, and the completion date on every copy. Long names shrink
                automatically to fit. Accepted files: PDF, PNG, or JPG, up to{' '}
                {MAX_DESIGN_SIZE_MB} MB.{' '}
                <a
                  href={LAYOUT_GUIDE_URL}
                  download
                  className="font-semibold text-primary-600 dark:text-primary-350 hover:underline"
                >
                  Download the layout guide
                </a>{' '}
                to see exactly which area to keep free.
              </p>
            </div>

            {pdfKey ? (
              <div className="flex items-center justify-between gap-3">
                <div className="flex items-center gap-3 min-w-0">
                  <span className="w-9 h-9 rounded-lg bg-error-500/10 inline-flex items-center justify-center">
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                      <path
                        d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z"
                        stroke={palette.error['500']}
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                      <polyline
                        points="14,2 14,8 20,8"
                        stroke={palette.error['500']}
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  </span>
                  <div className="min-w-0">
                    <p
                      className="text-sm font-semibold text-text-primary truncate"
                      style={{ fontFamily: 'Poppins, sans-serif' }}
                    >
                      {pdfName}
                    </p>
                    <p className="text-xs text-text-secondary">Design uploaded</p>
                  </div>
                </div>
                <button
                  type="button"
                  onClick={handleRemovePdf}
                  className="text-xs font-semibold text-error-500 hover:underline active:scale-[0.96] transition-[opacity,transform] duration-150"
                >
                  Remove
                </button>
              </div>
            ) : (
              <DropZone
                onFilesSelected={handlePdfSelected}
                accept="application/pdf,image/png,image/jpeg"
                maxSizeMB={MAX_DESIGN_SIZE_MB}
                label={
                  uploading
                    ? `Uploading… ${uploadProgress}%`
                    : 'Upload your certificate design here'
                }
              />
            )}
            {uploadError && (
              <p role="alert" className="text-xs text-error-500 mt-2">
                {uploadError}
              </p>
            )}

            {/* Live sample preview — exactly what learners will receive */}
            {pdfKey && (
              <div data-testid="certificate-preview">
                <p className="text-xs font-semibold text-text-secondary mb-2">
                  Preview with sample data — this is what every learner&rsquo;s
                  certificate will look like:
                </p>
                {previewLoading && (
                  <p className="text-xs text-text-secondary">Generating preview…</p>
                )}
                {previewError && (
                  <p role="alert" className="text-xs text-error-500">
                    {previewError}
                  </p>
                )}
                {previewUrl && !previewLoading && (
                  <embed
                    src={previewUrl}
                    type="application/pdf"
                    className="w-full rounded-xl border border-border-primary/20"
                    style={{ height: '380px' }}
                    data-testid="certificate-preview-embed"
                  />
                )}
              </div>
            )}
          </div>
        )}
      </div>
      )}
    </StepCard>
  );
}
